import { useRef } from 'react';
import {
  Button,
  FlexGrid,
  FlexGridColumn,
  Icon,
  Stack,
  Text,
  uniqueId,
} from '@hs-baumappe/legacy-ui';
import { ArrayHelpers, FieldArray, useField } from 'formik';
import { useTranslation } from 'react-i18next';

import { getSAPEmployeeRoleDefaults, OvertimeFormEmployee } from '../../form';
import EmployeeCard from './components/EmployeeCard';

export type Employee = {
  id: string;
  name: string;
  role: string;
};

interface EmployeesSectionProps {
  employees: Employee[];
  employeeAutomaticRoleSelection: boolean;
}

export default function EmployeesSection({
  employees,
  employeeAutomaticRoleSelection,
}: EmployeesSectionProps): JSX.Element {
  const { t } = useTranslation();
  const employeesTitleId = useRef(uniqueId('employees-title-'));

  const [employeesField] = useField<OvertimeFormEmployee[]>('employees');
  const employeesValue = employeesField.value;
  const employeesRemovable = employeesValue.length > 1;
  const latestEmployee = employeesValue[employeesValue.length - 1];

  const employeeNames = employees.map((employee) => employee.name);

  return (
    <FieldArray name="employees">
      {(arrayHelpers: ArrayHelpers<OvertimeFormEmployee[]>) => (
        <Stack gap="medium" role="group" aria-labelledby={employeesTitleId.current}>
          <Text id={employeesTitleId.current} variant="title-small" color="muted">
            {t('overtimeForm.employee.title')}
          </Text>

          <Stack gap="medium">
            {employeesValue.map((_, index) => (
              <EmployeeCard
                // eslint-disable-next-line react/no-array-index-key
                key={`employee-${index}`}
                index={index}
                employees={employees}
                employeeNames={employeeNames}
                employeeAutomaticRoleSelection={employeeAutomaticRoleSelection}
                onRemoveButtonClick={
                  employeesRemovable ? () => arrayHelpers.remove(index) : undefined
                }
              />
            ))}
          </Stack>

          <FlexGrid>
            <FlexGridColumn sm={6}>
              <Text variant="body-small">{t('overtimeForm.addAnotherEmployeeHintText')}</Text>
            </FlexGridColumn>
            <FlexGridColumn sm={6}>
              <Button
                type="button"
                onClick={() =>
                  latestEmployee
                    ? arrayHelpers.push({
                        ...latestEmployee,
                        employeeId: '',
                        fullName: '',
                        ...getSAPEmployeeRoleDefaults(employeeAutomaticRoleSelection),
                        sapRole: undefined,
                      } as OvertimeFormEmployee)
                    : undefined
                }
                fullWidth
                endIcon={<Icon name="add" />}
              >
                {t('overtimeForm.addAnotherEmployeeButton')}
              </Button>
            </FlexGridColumn>
          </FlexGrid>
        </Stack>
      )}
    </FieldArray>
  );
}
